import { useMemo, useState } from "react";
import { Button, Card, Input, Label, StatusBadge, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { calendarDateInputToIso, formatCalendarDate } from "@/utils/datetime";
import {
  useConfirmVendorTermProposal,
  useDeclineVendorTermProposal,
  useVendorTermProposals,
} from "@/hooks/portfolioAdmin/useRegistries";
import type {
  VendorContractTermDto,
  VendorContractTermProposalDto,
} from "@/api/portfolioAdmin/types";
import { describeBand } from "./contractTermTierForm";

// Step 6 W5 — the review side of the bridge.
//
// The schedule Phoenix wrote and the vendor countersigned has always carried
// a rate table. It now arrives here as a PROPOSAL: a signed PDF must never
// silently change what Phoenix bills, so nothing bills differently until
// someone with the terms-update permission confirms one.
//
// The confirm affordance is a FORM, not a button. The effective date is
// editable, pre-filled from the schedule's Engagement Start Date where it had
// one, and the "must be after the current terms" constraint is shown BEFORE
// submit rather than discovered as a server error.

/** ISO instant → the `yyyy-MM-dd` a date input wants, in UTC. An effective
 *  date is a wall-calendar value; localizing it would slide it a day. */
function toDateInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

export function TermProposalSection({
  vendorId,
  canEdit,
  current,
}: {
  vendorId: string;
  canEdit: boolean;
  /** The terms in force, if any — its effective date is the floor a new
   *  one has to clear. */
  current: VendorContractTermDto | null;
}) {
  const proposalsQuery = useVendorTermProposals(vendorId);
  const pending = useMemo(
    () => (proposalsQuery.data ?? []).filter((p) => p.status === "pending"),
    [proposalsQuery.data],
  );

  if (pending.length === 0) return null;

  return (
    <Card data-testid="term-proposals-card">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
        From a signed schedule
      </h3>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        {pending.length === 1 ? "A" : `${pending.length}`} countersigned
        compensation schedule{pending.length === 1 ? " has" : "s have"} been
        accepted for this vendor. Nothing bills differently until you confirm
        the terms below.
      </p>
      <div className="flex flex-col gap-space-3">
        {pending.map((p) => (
          <ProposalRow
            key={p.id}
            vendorId={vendorId}
            proposal={p}
            canEdit={canEdit}
            current={current}
          />
        ))}
      </div>
    </Card>
  );
}

function ProposalRow({
  vendorId,
  proposal,
  canEdit,
  current,
}: {
  vendorId: string;
  proposal: VendorContractTermProposalDto;
  canEdit: boolean;
  current: VendorContractTermDto | null;
}) {
  const toast = useToast();
  const confirm = useConfirmVendorTermProposal(vendorId);
  const decline = useDeclineVendorTermProposal(vendorId);
  const [open, setOpen] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(() =>
    toDateInput(proposal.proposedEffectiveFrom),
  );
  const [notes, setNotes] = useState("");
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  // The constraint the create path enforces, surfaced BEFORE submit. It is
  // strictly-later, not later-or-equal, because the current row's effective
  // date is where supersession stamps its end.
  const floor = current ? toDateInput(current.effectiveFrom) : null;
  const tooEarly = floor !== null && effectiveFrom !== "" && effectiveFrom <= floor;
  const canSubmit = effectiveFrom !== "" && !tooEarly && !confirm.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await confirm.mutateAsync({
        proposalId: proposal.id,
        body: {
          effectiveFrom: calendarDateInputToIso(effectiveFrom)!,
          notes: notes.trim() === "" ? null : notes.trim(),
        },
      });
      toast.success("Contract terms saved from the signed schedule.");
      setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const submitDecline = async () => {
    if (declineReason.trim() === "") return;
    try {
      await decline.mutateAsync({ proposalId: proposal.id, reason: declineReason.trim() });
      toast.success("Proposal declined.");
      setDeclining(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div
      className="rounded-md border border-gold/40 bg-gold/5 p-space-3 flex flex-col gap-space-2"
      data-testid={`term-proposal-${proposal.id}`}
    >
      <div className="flex items-center gap-space-2 flex-wrap">
        <StatusBadge tone="warn">Awaiting confirmation</StatusBadge>
        <span className="font-display font-semibold text-lead">
          Tiered — {proposal.tiers.length} band
          {proposal.tiers.length === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="m-0 pl-space-4 text-small text-onyx">
        {proposal.tiers.map((b) => (
          <li key={b.id}>{describeBand(b)}</li>
        ))}
      </ul>
      <p className="text-caption text-gray-mid m-0">
        {proposal.proposedEffectiveFrom
          ? `The schedule's engagement start date is ${formatCalendarDate(proposal.proposedEffectiveFrom)}.`
          : "The schedule did not carry an engagement start date — choose one before confirming."}
      </p>

      {canEdit && !open && !declining && (
        <div className="flex gap-space-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setOpen(true)}
            data-testid={`review-proposal-${proposal.id}`}
          >
            Review &amp; confirm
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setDeclining(true)}>
            Decline
          </Button>
        </div>
      )}

      {open && (
        <div className="flex flex-col gap-space-2 mt-space-1" data-testid="confirm-proposal-form">
          <div className="flex flex-col gap-space-1 w-52">
            <Label htmlFor={`proposal-effective-${proposal.id}`}>Effective from</Label>
            <Input
              id={`proposal-effective-${proposal.id}`}
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              data-testid="proposal-effective-from"
            />
          </div>
          {floor && (
            <p className="text-caption text-gray-mid m-0">
              Must be after {formatCalendarDate(current!.effectiveFrom)}, when the
              current terms took effect.
            </p>
          )}
          {tooEarly && (
            <p
              className="text-small text-status-error m-0"
              role="alert"
              data-testid="proposal-date-too-early"
            >
              Choose a date after {formatCalendarDate(current!.effectiveFrom)} — the
              current terms took effect then, and a new one has to start later.
            </p>
          )}
          <div className="flex flex-col gap-space-1">
            <Label htmlFor={`proposal-notes-${proposal.id}`}>Notes (optional)</Label>
            <Textarea
              id={`proposal-notes-${proposal.id}`}
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Defaults to a note naming the signed schedule."
            />
          </div>
          <div className="flex gap-space-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!canSubmit}
              onClick={() => void submit()}
              data-testid="confirm-proposal"
            >
              {confirm.isPending ? "Saving…" : "Confirm terms"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={confirm.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {declining && (
        <div className="flex flex-col gap-space-2 mt-space-1" data-testid="decline-proposal-form">
          <div className="flex flex-col gap-space-1">
            <Label htmlFor={`proposal-decline-${proposal.id}`}>Why are you declining?</Label>
            <Input
              id={`proposal-decline-${proposal.id}`}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. superseded by a renegotiated schedule"
              data-testid="proposal-decline-reason"
            />
          </div>
          <div className="flex gap-space-2">
            <Button
              variant="primary"
              size="sm"
              disabled={declineReason.trim() === "" || decline.isPending}
              onClick={() => void submitDecline()}
              data-testid="decline-proposal"
            >
              {decline.isPending ? "Declining…" : "Decline"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeclining(false)}
              disabled={decline.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
